import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getPlatformsByAccount } from "../../features/platforms/platformsSlice";
import moment from "moment";
import NotFoundMessage from "../../components/common/Modal/NotFoundMessage";
import { Icons } from "../../constants/icon-library";
import { capitalize } from "../../constants/constants";

const CLIENT_ID_DISPLAY_LENGTH = 20;

function truncateClientId(clientId) {
  if (!clientId) return "";
  if (clientId.length <= CLIENT_ID_DISPLAY_LENGTH) return clientId;
  return clientId.slice(0, CLIENT_ID_DISPLAY_LENGTH) + "...";
}

const Platform = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    platforms = [],
    loading,
    error,
  } = useSelector((state) => state.platforms || {});
  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    if (user?.org) {
      dispatch(getPlatformsByAccount(user.org));
    }
  }, [user, dispatch]);

  const showPlatformsNotFoundError = platforms.length === 0;

  return (
    <div className="content">
      <div className="d-flex flex-column pt-3">
        {!showPlatformsNotFoundError && (
          <>
            <div className="d-flex justify-content-end">
              <button
                className="btn btn-primary mb-1"
                onClick={() => navigate(`/platform/create`)}
              >
                <i
                  className={`wb ${Icons("add")} fs-8 me-2 mt-3 align-middle`}
                ></i>
                Add Platform
              </button>
            </div>

            <div className="w-100" data-breakpoint="lg">
              <div className="navbar-vertical-content">
                <p className="text-uppercase h4 text-body-tertiary text-opacity-85 m-0 fw-bold">
                  Platforms
                </p>

                {!loading && platforms.length === 0 && (
                  <p className="text-danger mt-2 mb-2">
                    No account added. Add a new account to continue.
                  </p>
                )}

                <div className="d-flex mt-3">
                  <div className="row">
                    {[...platforms].reverse().map((acc) => (
                      <div
                        key={acc.uuid}
                        className="assistant-card col-auto d-flex flex-column m-2 p-4 rounded shadow-sm"
                        style={{ minWidth: 320, maxWidth: 340, width: 340 }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="h3 mb-0">{acc.name}</div>
                        </div>

                        <div className="my-2 border-top pt-2">
                          <div className="d-flex align-items-center text-muted mb-2">
                            <i
                              className={`wb ${Icons("clock")} fs-8 me-2 `}
                            ></i>
                            <span>
                              {moment
                                .utc(acc.createdAt)
                                .format("MMM D, YYYY • h:mm A")}
                            </span>
                          </div>

                          <div className="d-flex align-items-center mb-2">
                            <i
                              className={`wb ${Icons("client")} fs-8 me-2 `}
                            ></i>
                            <span
                              className="text-break text-muted"
                              style={{
                                maxWidth: 170,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "inline-block",
                                verticalAlign: "bottom",
                                whiteSpace: "nowrap",
                              }}
                              title={acc.clientId}
                            >
                              Client ID: {truncateClientId(acc.clientId)}
                            </span>
                          </div>

                          <div className="d-flex align-items-center text-muted mb-2">
                            <i
                              className={`wb ${Icons("platform")} fs-8 me-2 `}
                            ></i>
                            <span>Type: {capitalize(acc.type) || "Other"}</span>
                          </div>
                        </div>

                        <div className="d-flex justify-content-end align-items-center mt-7">
                          <button
                            className="btn btn-primary px-4 me-2"
                            onClick={() =>
                              navigate(
                                `/platform/create?edit=${acc.uuid}`
                              )
                            }
                          >
                            <div className="d-flex p-0 align-items-center">
                              <i
                                className={`wb ${Icons("edit")} fs-8 me-2 `}
                              ></i>
                              <span className="text-nowrap">Edit</span>
                            </div>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {error && (
                  <p className="text-danger mt-3">
                    Error loading platforms: {error}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
        {showPlatformsNotFoundError && (
          <NotFoundMessage
            message={`No platforms found. Please create one.`}
            onAddNew={() => navigate(`/platform/create`)}
            buttonMessage={`Add Platform`}
          />
        )}
      </div>
    </div>
  );
};

export default Platform;
